import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { FormikTextField, FormikPhoneField } from '@lendica/components';
import Box from '@mui/material/Box';
import LoadingButton from '@mui/lab/LoadingButton';

interface LeadFormStepTwoValues {
    email: string;
    firstName: string;
    lastName: string;
    company: string;
    phone?: string;
}

interface LeadFormStepTwoProps {
    initialValues: LeadFormStepTwoValues;
    onSubmit: (values: LeadFormStepTwoValues) => void;
    onBack: () => void;
    requirePhone?: boolean;
}

export const LeadFormStepTwo = ({
    initialValues,
    onSubmit,
    onBack,
    requirePhone = true,
}: LeadFormStepTwoProps) => {
    const phoneFieldSchema = yup
        .string()
        .test('len', 'Please provide a valid phone number', val => {
            if (!val) return !requirePhone;
            return val.length === 10;
        })
        .transform(value => (value && value.trim() ? value.trim() : undefined));

    const validationSchema = yup.object({
        email: yup.string().required(), // Required but will be disabled
        firstName: yup.string().required('First name is required'),
        lastName: yup.string().required('Last name is required'),
        company: yup.string().required('Company name is required'),
        phone: requirePhone
            ? phoneFieldSchema.required('Phone number is required')
            : phoneFieldSchema.notRequired(),
    });

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({ isSubmitting, isValid, dirty }) => (
                <Box
                    component={Form}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        width: 380,
                        maxWidth: '100%', // Prevent overflow on very small screens
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: { xs: 1, md: 2 },
                        }}
                    >
                        <FormikTextField
                            name="firstName"
                            label="First name"
                            type="text"
                            helperText=" " // To keep the same height as the other fields, and reserve space for error text
                            fullWidth
                            required
                            size="medium"
                        />
                        <FormikTextField
                            name="lastName"
                            label="Last name"
                            type="text"
                            helperText=" "
                            fullWidth
                            required
                            size="medium"
                        />
                    </Box>

                    <FormikTextField
                        name="email"
                        label="Work email"
                        type="email"
                        helperText=" "
                        fullWidth
                        disabled
                        size="medium"
                    />

                    <FormikTextField
                        name="company"
                        label="Company name"
                        type="text"
                        helperText="Use your company's legal name."
                        fullWidth
                        required
                        size="medium"
                    />
                    {requirePhone && (
                        <FormikPhoneField
                            name="phone"
                            label="Phone number"
                            helperText="We will send you a pin to verify your number."
                            fullWidth
                            required
                            size="medium"
                        />
                    )}

                    <LoadingButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        disabled={isSubmitting || !isValid || !dirty}
                        loading={isSubmitting}
                        sx={{
                            mt: 2,
                        }}
                    >
                        Next
                    </LoadingButton>
                </Box>
            )}
        </Formik>
    );
};
